import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldAlert, Eye, RefreshCw, Loader2, Users, User, Clock,
} from "lucide-react";

/**
 * /admin/impersonation-log — read-only audit trail of every admin
 * "view-as client" session. Useful for SOC2/ISO27001 compliance.
 */
export default function ImpersonationLogPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [clientFilter, setClientFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: "200" });
      if (clientFilter.trim()) params.set("client_id", clientFilter.trim());
      if (adminFilter.trim()) params.set("admin_email", adminFilter.trim());
      const { data } = await api.get(
        `/admin/impersonation/events?${params}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    } finally { setLoading(false); }
  }, [days, clientFilter, adminFilter]);
  useEffect(() => { load(); }, [load]);

  const events = data?.events || [];
  const totals = data?.totals || {};

  return (
    <div className="p-6 max-w-6xl" data-testid="impersonation-log-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-amber-100 text-amber-700">
          <ShieldAlert className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">COMPLIANCE</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            View-as audit log
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Every admin session that viewed a client portal via the
            branded login flow. Tab-scoped sessions auto-end on Exit.
          </p>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={load} disabled={loading}
          data-testid="impersonation-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                   : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {/* ── Totals ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Pill label="Sessions" value={totals.count ?? 0}
          icon={Eye} testid="impersonation-total-count" />
        <Pill label="Open now" value={totals.open_now ?? 0}
          icon={Clock} accent={(totals.open_now ?? 0) > 0 ? "text-amber-700" : ""}
          testid="impersonation-open-now" />
        <Pill label="Distinct admins"
          value={(totals.by_admin || []).length}
          icon={User} />
        <Pill label="Distinct clients"
          value={(totals.by_client || []).length}
          icon={Users} />
      </div>

      {/* ── Filters ── */}
      <Card className="rounded-sm border-zinc-200 mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
            Days
            <Input type="number" min="1" max="365" value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10) || 30)}
              className="mt-1 h-9 w-24 text-sm font-mono"
              data-testid="impersonation-days" />
          </label>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex-1 min-w-[180px]">
            Filter by admin email
            <Input type="search" value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
              placeholder="admin@…"
              className="mt-1 h-9 text-sm"
              data-testid="impersonation-admin-filter" />
          </label>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex-1 min-w-[180px]">
            Filter by client_id
            <Input type="search" value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              placeholder="client_abc123…"
              className="mt-1 h-9 text-sm font-mono"
              data-testid="impersonation-client-filter" />
          </label>
        </CardContent>
      </Card>

      {/* ── Top breakdowns ── */}
      {!loading && events.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
          <BreakdownCard title="Top admins (this window)"
            rows={(totals.by_admin || []).slice(0, 5)}
            labelKey="email" countLabel="sessions"
            testid="impersonation-by-admin" />
          <BreakdownCard title="Top clients viewed"
            rows={(totals.by_client || []).slice(0, 5)}
            labelKey="client_company" countLabel="sessions"
            testid="impersonation-by-client" />
        </div>
      )}

      {/* ── Table ── */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-0">
          <div className="grid grid-cols-[1fr_1fr_180px_120px_90px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
            <span>Admin</span>
            <span>Viewed client</span>
            <span>Started</span>
            <span>Duration</span>
            <span className="text-right">Status</span>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : events.length === 0 ? (
            <div className="p-8 text-center text-sm italic text-zinc-500"
              data-testid="impersonation-empty">
              No view-as sessions in this window.
            </div>
          ) : (
            events.map((e) => (
              <div key={e.event_id}
                className="grid grid-cols-[1fr_1fr_180px_120px_90px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50"
                data-testid={`impersonation-event-${e.event_id}`}>
                <span className="truncate font-semibold">{e.admin_email}</span>
                <span className="truncate">
                  {e.client_company}
                  <div className="text-[10px] text-zinc-400 font-mono truncate">
                    {e.client_id}
                  </div>
                </span>
                <span className="text-xs font-mono text-zinc-600">
                  {fmtDateTime(e.started_at)}
                </span>
                <span className="text-xs font-mono">
                  {e.ended_at
                    ? fmtDuration(e.duration_seconds)
                    : <span className="text-amber-700 font-semibold">open</span>}
                </span>
                <span className="text-right">
                  {e.ended_at ? (
                    <Badge variant="outline"
                      className="rounded-sm text-[9px] bg-zinc-100 text-zinc-700 border-zinc-200">
                      CLOSED
                    </Badge>
                  ) : (
                    <Badge variant="outline"
                      className="rounded-sm text-[9px] bg-amber-100 text-amber-800 border-amber-200 animate-pulse">
                      ACTIVE
                    </Badge>
                  )}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Pill({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white"
      data-testid={testid}>
      <div className="label-overline text-[9px] flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className={`font-display font-bold text-xl mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

function BreakdownCard({ title, rows, labelKey, countLabel, testid }) {
  return (
    <Card className="rounded-sm border-zinc-200" data-testid={testid}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 mb-2">
          {title}
        </div>
        {rows.length === 0 ? (
          <div className="text-xs italic text-zinc-400">No data.</div>
        ) : rows.map((r, i) => (
          <div key={`${r[labelKey] || "row"}-${i}`}
            className="flex items-center justify-between py-1 text-xs">
            <span className="truncate">{r[labelKey]}</span>
            <span className="font-mono text-zinc-600 ml-2">
              {r.count} {countLabel}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 19);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtDuration(seconds) {
  if (seconds == null) return "—";
  const s = Number(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
